import { effect, inject, Injectable } from "@angular/core";

import { ChartOptionsService } from "@features/charts/services/chart-options.service";
import { CacheId, MessageType } from "@shared/enums";
import { getContractualMonth } from "@shared/functions/date.functions";
import { HistoryRecord, PlanRecord } from "@shared/models";
import { ErrorHandlingService } from "@shared/services";
import { ChartData } from "chart.js";

interface ChartDataRecord {
  effectivePeriod: number;
  vmax: number;
  proj: number;
  imax: number;
  work?: number;
  pvds: number;
  training: number;
  sick: number;
}

@Injectable({
  providedIn: "root",
})
export class ChartDataService {
  chartOptionsService = inject(ChartOptionsService);
  errorHandlingService = inject(ErrorHandlingService);
  loadEmptyChartData(): ChartData<"bar"> {
    const labels = () => {
      let monthIndex = -11;
      const labels: string[] = [];
      for (let i = 0; i < 12; i++) {
        labels.push(getContractualMonth({ offset: monthIndex }).monthShort);
        monthIndex++;
      }
      return labels;
    };

    const chartColors = this.chartOptionsService.getChartColors();
    this.errorHandlingService.sendMessage(MessageType.NotFound);
    return {
      labels: labels(),
      datasets: [
        {
          type: "bar",
          label: "Empty",
          data: Array.from({ length: 12 }, () => 0),
          backgroundColor: chartColors[0].backgroundColor,
          borderWidth: 1,
        },
      ],
    };
  }

  getChartData(chartType: string, planData: boolean): ChartData<"bar"> {
    const storageKey = planData ? CacheId.ActivePlan : CacheId.History;
    const data = localStorage.getItem(storageKey);

    if (!data) {
      return this.loadEmptyChartData();
    }

    let parsedData: PlanRecord[] | HistoryRecord[];
    if (planData) {
      parsedData = JSON.parse(data).plans.slice(0, 12);
    } else {
      parsedData = JSON.parse(data).history;
    }

    if (!Array.isArray(parsedData)) {
      console.error("Invalid data format");
      return this.loadEmptyChartData();
    }
    const chartDataRecords: ChartDataRecord[] = parsedData.map((record) => {
      if ("vmaxMinutes" in record) {
        // HistoryRecord
        return {
          effectivePeriod: record.effectivePeriod,
          vmax: record.vmaxMinutes,
          proj: record.projMinutes,
          imax: record.imaxMinutes,
          work: record.daysWorked,
          pvds: record.usedPVDs,
          training: record.usedTraining,
          sick: record.usedSick,
        };
      } else {
        // PlanRecord
        return {
          effectivePeriod: record.effectivePeriod,
          vmax: record.vmax,
          proj: record.proj,
          imax: record.imax,
          pvds: record.plannedPVDs,
          training: record.plannedTraining,
          sick: record.plannedSick,
        };
      }
    });
    const chartValues = chartDataRecords.map((record) => {
      const value = record[chartType as keyof ChartDataRecord] || 0;
      if (chartType === "vmax" || chartType === "proj" || chartType === "imax") {
      return Math.round(value / 60 * 10) / 10;
      }
      return value;
    });

    const labels = chartDataRecords.map((record) => {
      const lastTwoNumbers = record.effectivePeriod % 100;
      return getContractualMonth({ month: lastTwoNumbers }).monthShort;
    });
    const chartColors = this.chartOptionsService.getChartColors();

    return {
      labels,
      datasets: [
        {
          type: "bar",
          label: chartType,
          data: chartValues,
          backgroundColor: chartColors[0].backgroundColor,
          borderWidth: 0,
        },
      ],
    };
  }
}
